import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import AppPaths from '../AppPaths';
import { useLocale } from '../contexts/LocaleContext';
import * as events from '../lib/analytics/events';
import { HARDWARE_FIRST_MAX_UNITS_PER_CART } from '../lib/hardwareFirst';
import { getProductsAndVariantsBySku } from '../lib/product';
import { cartActions } from '../reducers/storeCart';
import * as types from '../types';
import useHardwareFirstCheckout from './useHardwareFirstCheckout';

/** The link params this hook consumes; everything else (code, UTMs, click ids) is passed through. */
const ITEMS_PARAM = 'items';
const LEGACY_DEVICE_PARAM = 'item';

/** `items=F3B-MBCUA-BL-M:1,M1-XX:2` as [sku, quantity] pairs, keeping only SKUs the catalog knows. */
function parseItems(value: string | null, knownSkus: Set<string>): Array<[string, number]> {
  if (!value) return [];
  return value.split(',').flatMap((entry) => {
    const [rawSku, rawQuantity] = entry.split(':');
    const sku = rawSku?.trim().toUpperCase();
    if (!sku || !knownSkus.has(sku)) return [];
    const quantity = Math.min(Math.max(parseInt(rawQuantity ?? '1', 10) || 1, 1), HARDWARE_FIRST_MAX_UNITS_PER_CART);
    return [[sku, quantity] as [string, number]];
  });
}

const withParams = (path: string, params: URLSearchParams) => {
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

/**
 * Cart-abandonment emails link to /bag?items=<sku:qty,...>&code=<promo>&utm_*. The bag lives in this
 * browser's localStorage, so when the email is opened on another device the bag is empty. On the
 * hardware-first checkout the cart is just collar SKUs, so the link's items rebuild it as it was and the
 * shopper stays on the bag. Otherwise (control checkout, or no usable items) the shopper is sent to
 * /shop. The promo code, UTMs and any other params are kept either way. A bag that has items, or a
 * plain /bag visit, is left alone.
 */
export default function useRestoreCartFromLink(isCartEmpty: boolean) {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { currency } = useLocale();
  const hardwareFirstEnrolled = useHardwareFirstCheckout();
  const products = useSelector((state: types.AppState) => state.config.products);
  // a link is acted on once; the replace below drops its params, but a re-run before that lands must not add twice
  const handled = useRef(false);

  useEffect(() => {
    if (!isCartEmpty || handled.current) return;
    const params = new URLSearchParams(location.search);
    if (!params.has(ITEMS_PARAM) && !params.has(LEGACY_DEVICE_PARAM)) return;
    handled.current = true;

    const items = parseItems(params.get(ITEMS_PARAM), new Set(getProductsAndVariantsBySku(products).keys()));
    params.delete(ITEMS_PARAM);
    params.delete(LEGACY_DEVICE_PARAM);

    if (hardwareFirstEnrolled && items.length > 0) {
      items.forEach(([sku, quantity]) => {
        dispatch(cartActions.addSingleLineCartItem({ sku, quantity, currency }));
        events.hardwareFirstProductAdded({ sku, currency, source: 'abandoned_cart_link' });
      });
      events.abandonedCartLink.restored(items.reduce((sum, [, quantity]) => sum + quantity, 0));
      history.replace(withParams(AppPaths.Bag, params));
      return;
    }

    events.abandonedCartLink.sentToShop();
    history.replace(withParams(AppPaths.Shop, params));
  }, [isCartEmpty, location.search, history, dispatch, currency, hardwareFirstEnrolled, products]);
}
